/**
 * Factory Service
 *
 * Handles all factory-related API operations with Zod validation.
 */

import { fetchWithAuth } from '@/lib/api-client';
import {
  type Factory,
  type FactoryCreateForm,
  type FactoryUpdateForm,
  type FactoryFilter,
  type FactoryListResponse,
  factorySchema,
  factoryListResponseSchema,
} from '@/schemas/factory/factory.schemas';
import { shouldUseMockData } from '@/lib/featureFlags';
import {
  mockListFactories,
  mockGetFactory,
  mockCreateFactory,
  mockUpdateFactory,
  mockDeleteFactory,
} from '@/tests/factory.service.mock';
import type { FloorPlanSettings } from '@/types/floorplan';

/**
 * List all factories with filtering and pagination
 */
export async function listFactories(filters: FactoryFilter): Promise<FactoryListResponse> {
  if (shouldUseMockData()) {
    return mockListFactories(filters);
  }

  const queryParams = new URLSearchParams({
    page: filters.page?.toString() || '1',
    limit: filters.limit?.toString() || '20',
  });

  if (filters.search) {
    queryParams.append('search', filters.search);
  }
  if (filters.country) {
    queryParams.append('country', filters.country);
  }
  if (filters.isActive !== undefined) {
    queryParams.append('isActive', filters.isActive.toString());
  }

  const response = await fetchWithAuth<FactoryListResponse>(
    `/api/factories?${queryParams.toString()}`
  );

  return factoryListResponseSchema.parse(response);
}

/**
 * Get factory by ID
 */
export async function getFactoryById(factoryId: string): Promise<Factory> {
  if (shouldUseMockData()) {
    return mockGetFactory(factoryId);
  }

  const response = await fetchWithAuth<Factory>(`/api/factories/${factoryId}`);
  return factorySchema.parse(response);
}

/**
 * Create a new factory
 */
export async function createFactory(data: FactoryCreateForm): Promise<Factory> {
  if (shouldUseMockData()) {
    return mockCreateFactory(data);
  }

  const response = await fetchWithAuth<Factory>('/api/factories', {
    method: 'POST',
    body: data,
  });

  return factorySchema.parse(response);
}

/**
 * Update an existing factory
 */
export async function updateFactory(factoryId: string, data: FactoryUpdateForm): Promise<Factory> {
  if (shouldUseMockData()) {
    return mockUpdateFactory(factoryId, data);
  }

  const response = await fetchWithAuth<Factory>(`/api/factories/${factoryId}`, {
    method: 'PUT',
    body: data,
  });

  return factorySchema.parse(response);
}

/**
 * Delete a factory (soft delete)
 * Pass force=true to bypass 409 blocker checks (after user explicitly confirms)
 */
export async function deleteFactory(
  factoryId: string,
  force?: boolean
): Promise<{ success: boolean; message: string }> {
  if (shouldUseMockData()) {
    return mockDeleteFactory(factoryId);
  }

  const url = force ? `/api/factories/${factoryId}?force=true` : `/api/factories/${factoryId}`;
  return fetchWithAuth<{ success: boolean; message: string }>(url, {
    method: 'DELETE',
  });
}

/**
 * Update floor plan calibration settings for a factory.
 * Requires ADMIN_FACTORY permission.
 */
export async function updateFloorPlanSettings(
  factoryId: string,
  settings: FloorPlanSettings
): Promise<FloorPlanSettings> {
  return fetchWithAuth<FloorPlanSettings>(`/api/factories/${factoryId}/floorplan-settings`, {
    method: 'PUT',
    body: settings,
  });
}

/**
 * Get floor plan calibration settings for a factory.
 * Returns null if no settings are configured.
 */
export async function getFloorPlanSettings(factoryId: string): Promise<FloorPlanSettings | null> {
  try {
    return await fetchWithAuth<FloorPlanSettings>(`/api/factories/${factoryId}/floorplan-settings`);
  } catch (error) {
    // 404 = no settings configured, 400 = factory ID validation mismatch — return null
    if (
      error instanceof Error &&
      'statusCode' in error &&
      [400, 404].includes((error as { statusCode: number }).statusCode)
    ) {
      return null;
    }
    throw error;
  }
}

/**
 * Upload floor plan image for a factory.
 * Returns the uploaded image URL and dimensions.
 */
export async function uploadFloorPlan(
  factoryId: string,
  file: File
): Promise<{ url: string; width: number; height: number }> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetchWithAuth<{ url: string; width: number; height: number }>(
    `/api/factories/${factoryId}/floorplan`,
    {
      method: 'POST',
      body: formData,
    }
  );

  return response;
}
